// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Types --------------------------------------------------------------------------------------
export type TyPasswordDetails = { name: string; password: string };

type Props = { onClose: () => void; details?: unknown };
// --- Logic --------------------------------------------------------------------------------------
/**
 * The one screen in this product where a password is ever shown.
 *
 * Passwords are generated and handed over — there is no email link, no reset token and no
 * self-service reset anywhere in Lozicore — so this is read out loud or pasted into a message once,
 * and then it is gone. Hence the size, the spacing, the `user-select: all` and the copy button: every
 * one of them is here to stop somebody transcribing eight digits wrongly over the phone.
 */
export default function PasswordPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { name, password } = (details ?? { name: "", password: "" }) as TyPasswordDetails;
    const [isCopied, setIsCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(password);
            setIsCopied(true);
        } catch {
            // No clipboard permission, or an insecure origin. The value is on screen and selectable,
            // which is the fallback — a failed copy must not look like a failed reset.
            setIsCopied(false);
        }
    };

    return (
        <section className="popup-card password-popup">
            <header className="password-popup__head">
                <h2 className="password-popup__title">{t.password.title.replace("{name}", name)}</h2>
                <button type="button" className="password-popup__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="password-popup__secret">
                <span className="password-popup__value">{password}</span>
                <button type="button" className="password-popup__copy" onClick={handleCopy} aria-label={t.common.copy}>
                    <Icon name={isCopied ? "check" : "copy"} size={18} />
                </button>
            </div>

            <p className="password-popup__hint">{isCopied ? t.common.copied : t.password.hint}</p>

            <button type="button" className="password-popup__done" onClick={onClose}>
                {t.common.done}
            </button>
        </section>
    );
}
